import {
    disableElementTransition,
    enableElementTransition,
    getScrollPercent,
    getSiteVersion,
    SiteVersion,
} from "../../utils";
import {
    PopupTrigger,
    PopupType,
    popupButtonSelector,
    readIntegerAttribute,
    resolvePopupTrigger,
    resolvePopupType,
    isPopupOpen,
} from "./popup-config";
import type { PopupElement } from "./popup-config";
import { popupStorage } from "./popup-storage";

const TYPING_RETRY_DELAY_MS = 5000;
const TRANSITIONS_RESTORE_DELAY_MS = 100;

type TimerId = ReturnType<typeof setTimeout>;

export class Popup {
    private readonly lifetimeAbortController = new AbortController();
    private readonly attributesObserver: MutationObserver;
    private delayedOpenTimerId: TimerId | null = null;
    private transitionsRestoreTimerId: TimerId | null = null;
    private isAutoOpenDisabled = false;
    private wasFirstScreenScrolled = false;

    constructor(
        readonly element: PopupElement,
        private readonly onStateChange: () => void,
    ) {
        this.setTransitionsEnabled(true);
        this.removeStaleHideRecord();
        this.scheduleAutoOpen();

        this.element.addEventListener("close", this.syncState, {
            signal: this.lifetimeAbortController.signal,
        });

        this.attributesObserver = new MutationObserver(() => {
            this.removeStaleHideRecord();
            this.scheduleAutoOpen();
        });

        this.attributesObserver.observe(this.element, {
            attributes: true,
            attributeFilter: ["data-open-trigger"],
        });
    }

    get id(): string | null {
        return this.element.getAttribute("data-popup-id");
    }

    get type(): PopupType {
        return resolvePopupType(this.element);
    }

    get openTrigger(): PopupTrigger | null {
        return resolvePopupTrigger(this.element);
    }

    get isOpen(): boolean {
        return isPopupOpen(this.element);
    }

    get isDialog(): boolean {
        return this.element instanceof HTMLDialogElement;
    }

    get content(): HTMLElement | null {
        return this.element.querySelector(".popup__content");
    }

    get isHideable(): boolean {
        return this.element.hasAttribute("data-hide-duration");
    }

    get isLockingPageScroll(): boolean {
        if (!this.isOpen) {
            return false;
        }

        const type = this.type;

        return type === PopupType.MODAL || type === PopupType.SHEET;
    }

    show(): void {
        if (this.isOpen || this.isShowingBlocked) {
            return;
        }

        if (this.element instanceof HTMLDialogElement) {
            this.showDialog(this.element);
        } else {
            this.showStatic();
        }

        this.syncState();
    }

    close(): void {
        if (!this.isOpen) {
            return;
        }

        if (this.element instanceof HTMLDialogElement) {
            this.element.close();
            return;
        }

        this.element.removeAttribute("open");
        this.setStaticBackdropActive(false);
        this.element.dispatchEvent(new Event("close"));
    }

    toggle(): void {
        if (this.isOpen) {
            this.close();
        } else {
            this.show();
        }
    }

    dismiss(): void {
        this.close();

        if (!this.isHideable) {
            return;
        }

        this.markHidden();
        this.cancelAutoOpen();
        this.isAutoOpenDisabled = true;
    }

    markHidden(): void {
        const popupId = this.id;

        if (popupId !== null) {
            popupStorage.markHidden(popupId);
        }
    }

    isEventOnBackdrop(event: MouseEvent | PointerEvent): boolean {
        return this.isDialog
            && event.target === this.element
            && this.isPointOutside(event.clientX, event.clientY);
    }

    setTransitionsEnabled(isEnabled: boolean): void {
        for (const animatedElement of [this.element, this.staticBackdropElement]) {
            if (animatedElement === null) {
                continue;
            }

            if (isEnabled) {
                enableElementTransition(animatedElement);
            } else {
                disableElementTransition(animatedElement);
            }
        }
    }

    handleClick(event: MouseEvent, isPointerDownStartedOnBackdrop: boolean): void {
        const target = event.target;

        if (!(target instanceof Element)) {
            return;
        }

        if (this.isClickOnCloseButton(target) || this.isClickOnDismissingLink(target)) {
            this.dismiss();
            return;
        }

        const isBackdropDismissAllowed = this.type !== PopupType.NON_MODAL;

        if (isBackdropDismissAllowed && isPointerDownStartedOnBackdrop && this.isEventOnBackdrop(event)) {
            this.dismiss();
        }
    }

    handlePageScroll(): void {
        if (this.isAutoOpenDisabled || this.openTrigger !== PopupTrigger.SCROLL) {
            return;
        }

        const scrollTriggerPercent = readIntegerAttribute(this.element, "data-scroll-trigger-percent");

        if (scrollTriggerPercent !== null) {
            if (getScrollPercent() >= scrollTriggerPercent) {
                this.show();
            } else {
                this.close();
            }

            return;
        }

        if (getSiteVersion() === SiteVersion.Mobile) {
            return;
        }

        const isFirstScreenScrolled = window.innerHeight < window.scrollY;

        if (isFirstScreenScrolled === this.wasFirstScreenScrolled) {
            return;
        }

        this.wasFirstScreenScrolled = isFirstScreenScrolled;

        if (isFirstScreenScrolled) {
            this.show();
        } else {
            this.close();
        }
    }

    handleSiteVersionChange(): void {
        this.setTransitionsEnabled(false);
        this.close();

        if (this.transitionsRestoreTimerId !== null) {
            clearTimeout(this.transitionsRestoreTimerId);
        }

        this.transitionsRestoreTimerId = setTimeout(
            () => this.setTransitionsEnabled(true),
            TRANSITIONS_RESTORE_DELAY_MS,
        );
    }

    destroy(): void {
        this.cancelAutoOpen();

        if (this.transitionsRestoreTimerId !== null) {
            clearTimeout(this.transitionsRestoreTimerId);
        }

        this.attributesObserver.disconnect();
        this.lifetimeAbortController.abort();
    }

    private get hideDurationMilliseconds(): number {
        return readIntegerAttribute(this.element, "data-hide-duration") ?? 0;
    }

    private get isHiddenManually(): boolean {
        return this.element.hasAttribute("data-hidden");
    }

    private get isHiddenByUser(): boolean {
        const popupId = this.id;

        if (popupId === null) {
            return false;
        }

        // A record of a non-hideable popup is blocking it until the next page load
        const blockDurationMilliseconds = this.isHideable
            ? this.hideDurationMilliseconds
            : Number.POSITIVE_INFINITY;

        return popupStorage.isHiddenWithin(popupId, blockDurationMilliseconds);
    }

    private get isShowingBlocked(): boolean {
        return this.isHiddenManually || this.isHiddenByUser;
    }

    private get shouldOpenWithDelay(): boolean {
        const trigger = this.openTrigger;

        if (trigger === PopupTrigger.DELAY) {
            return true;
        }

        const hasScrollTriggerPercent = readIntegerAttribute(this.element, "data-scroll-trigger-percent") !== null;

        return trigger === PopupTrigger.SCROLL
            && !hasScrollTriggerPercent
            && getSiteVersion() === SiteVersion.Mobile;
    }

    private get isUserTyping(): boolean {
        const activeElement = document.activeElement;

        return activeElement !== null && activeElement.closest("input, select, textarea") !== null;
    }

    private get staticBackdropElement(): HTMLElement | null {
        if (this.isDialog) {
            return null;
        }

        const nextElement = this.element.nextElementSibling;

        return nextElement instanceof HTMLDivElement && nextElement.classList.contains("popup-backdrop")
            ? nextElement
            : null;
    }

    private showDialog(dialog: HTMLDialogElement): void {
        const type = this.type;
        const isNonBlockingType = type === PopupType.NON_MODAL || type === PopupType.DROPDOWN;

        if (isNonBlockingType) {
            dialog.show();
        } else {
            dialog.showModal();
        }
    }

    private showStatic(): void {
        this.element.setAttribute("open", "");
        this.setStaticBackdropActive(true);
    }

    private setStaticBackdropActive(isActive: boolean): void {
        this.staticBackdropElement?.classList.toggle("active", isActive);
    }

    private isPointOutside(clientX: number, clientY: number): boolean {
        const rect = this.element.getBoundingClientRect();

        return clientY < rect.top
            || clientY > rect.bottom
            || clientX < rect.left
            || clientX > rect.right;
    }

    private isClickOnCloseButton(target: Element): boolean {
        const closeButton = target.closest(".close-button");

        return closeButton !== null && this.element.contains(closeButton);
    }

    private isClickOnDismissingLink(target: Element): boolean {
        if (this.type === PopupType.NON_MODAL) {
            return false;
        }

        const link = target.closest("a");

        return link !== null && this.element.contains(link);
    }

    private scheduleAutoOpen(): void {
        this.cancelAutoOpen();
        this.isAutoOpenDisabled = false;

        if (this.shouldOpenWithDelay) {
            this.scheduleDelayedOpen();
        }
    }

    private cancelAutoOpen(): void {
        if (this.delayedOpenTimerId !== null) {
            clearTimeout(this.delayedOpenTimerId);
            this.delayedOpenTimerId = null;
        }
    }

    private scheduleDelayedOpen(): void {
        const tryOpen = (): void => {
            this.delayedOpenTimerId = null;

            if (this.isUserTyping) {
                this.delayedOpenTimerId = setTimeout(tryOpen, TYPING_RETRY_DELAY_MS);
                return;
            }

            this.show();
        };

        const showingDelayMilliseconds = readIntegerAttribute(this.element, "data-showing-delay") ?? 0;

        this.delayedOpenTimerId = setTimeout(tryOpen, showingDelayMilliseconds);
    }

    private removeStaleHideRecord(): void {
        const popupId = this.id;

        if (popupId === null) {
            return;
        }

        if (this.isHideable) {
            popupStorage.removeIfExpired(popupId, this.hideDurationMilliseconds);
        } else {
            popupStorage.remove(popupId);
        }
    }

    private readonly syncState = (): void => {
        this.syncTriggerButtons();
        this.onStateChange();
    };

    private syncTriggerButtons(): void {
        const popupId = this.id;

        if (popupId === null || this.openTrigger !== PopupTrigger.BUTTON) {
            return;
        }

        document
            .querySelectorAll(popupButtonSelector(popupId))
            .forEach(button => button.classList.toggle("active", this.isOpen));
    }
}
